import {
  ActionIcon, Badge, Button, Card, Center, Group, Loader, SegmentedControl,
  Stack, Table, Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconAlertTriangle, IconEdit, IconEye, IconPlus, IconTrash,
} from "@tabler/icons-react";
import { useState } from "react";
import { useRemove, useResource } from "../../../shared/api";
import {
  EmptyState, MoneyDisplay, PageHeader, ResourceForm,
  confirmDelete, type FormField,
} from "../../../shared/ui";
import { SupplierDetail } from "./SupplierDetail";

// Kategori options dinamik (procurement-categories'ten) → fonksiyonla üretilir.
function buildFields(
  categoryOptions: Array<{ value: string; label: string }>,
): FormField[] {
  return [
    { name: "name", label: "Ad", required: true },
    { name: "tax_id", label: "VKN / TCKN" },
    { name: "contact_name", label: "İletişim Kişisi" },
    { name: "phone", label: "Telefon", type: "phone" },
    { name: "email", label: "E-posta", type: "email" },
    { name: "address", label: "Adres", type: "textarea" },
    {
      name: "categories",
      label: "Tedarik Kategorileri (hangi gruplardan mal sağlar)",
      type: "multiselect",
      options: categoryOptions,
    },
    { name: "payment_terms_days", label: "Vade (gün)", type: "number", min: 0, defaultValue: 30 },
    {
      name: "credit_limit_minor",
      label: "Kredi Limiti (kuruş) — 0 = limitsiz",
      type: "number",
      min: 0,
      defaultValue: 0,
    },
    { name: "is_active", label: "Aktif", type: "switch", defaultValue: true },
  ];
}

export function SuppliersPage() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Tedarikçi listesi default sadece aktifleri gösterir; pasifler ("silinen"
  // ama bakiyesiz tedarikçiler) istenince segment ile görünür.
  const [statusFilter, setStatusFilter] = useState<"active" | "inactive" | "all">("active");
  const { data, isLoading } = useResource<any>("suppliers", {
    filters: statusFilter === "all" ? {} : { is_active: statusFilter === "active" },
  });
  const { data: catData } = useResource<any>("procurement-categories", {
    filters: { is_active: true },
  });
  const remove = useRemove("suppliers");
  const [opened, { open, close }] = useDisclosure(false);
  const [editing, setEditing] = useState<any | null>(null);
  const list: any[] = data?.results || data || [];
  const categoryOptions = ((catData?.results || catData || []) as any[]).map(
    (c) => ({ value: c.id, label: c.name }),
  );
  const fields = buildFields(categoryOptions);

  if (selectedId) {
    return <SupplierDetail supplierId={selectedId} onBack={() => setSelectedId(null)} />;
  }

  if (isLoading) return <Center h={300}><Loader /></Center>;

  function startEdit(s: any, e: React.MouseEvent) {
    e.stopPropagation();
    setEditing(s);
    open();
  }

  function startCreate() {
    setEditing(null);
    open();
  }

  async function handleDelete(id: string, e: React.MouseEvent) {
    e.stopPropagation();
    if (!(await confirmDelete("Tedarikçi silinsin mi?"))) return;
    try {
      await remove.mutateAsync(id);
      notifications.show({ message: "Silindi", color: "green" });
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    }
  }

  return (
    <Stack gap="md">
      <PageHeader
        title="Tedarikçiler"
        subtitle={`${list.length} tedarikçi`}
        actions={
          <Group gap="sm">
            <SegmentedControl
              size="xs"
              value={statusFilter}
              onChange={(v) => setStatusFilter(v as "active" | "inactive" | "all")}
              data={[
                { label: "Aktif", value: "active" },
                { label: "Pasif", value: "inactive" },
                { label: "Tümü", value: "all" },
              ]}
            />
            <Button leftSection={<IconPlus size={16} />} onClick={startCreate}>Yeni</Button>
          </Group>
        }
      />
      {list.length === 0 ? <EmptyState title="Tedarikçi yok" /> : (
        <Card padding="sm" radius="md" withBorder>
          <Table.ScrollContainer minWidth={640}>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Ad</Table.Th>
                <Table.Th>VKN</Table.Th>
                <Table.Th>Telefon</Table.Th>
                <Table.Th>Vade</Table.Th>
                <Table.Th ta="right">Bakiye</Table.Th>
                <Table.Th ta="right">Limit</Table.Th>
                <Table.Th>Durum</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {list.map((s: any) => {
                const balance = Number(s.balance_minor || 0);
                const limit = Number(s.credit_limit_minor || 0);
                const overLimit = Boolean(s.over_limit);
                const balColor = balance > 0 ? "red" : balance < 0 ? "green" : undefined;
                return (
                  <Table.Tr
                    key={s.id}
                    style={{ cursor: "pointer" }}
                    onClick={() => setSelectedId(s.id)}
                  >
                    <Table.Td fw={500}>
                      <Group gap={6}>
                        {s.name}
                        {overLimit && (
                          <Tooltip label="Kredi limiti aşıldı">
                            <IconAlertTriangle size={14} color="var(--mantine-color-red-6)" />
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>{s.tax_id || "-"}</Table.Td>
                    <Table.Td>{s.phone || "-"}</Table.Td>
                    <Table.Td>{s.payment_terms_days || 0} gün</Table.Td>
                    <Table.Td ta="right" fw={500}>
                      <MoneyDisplay minor={balance} {...(balColor ? { c: balColor } : {})} />
                    </Table.Td>
                    <Table.Td ta="right">
                      {limit > 0 ? <MoneyDisplay minor={limit} /> : <span style={{ color: "var(--mantine-color-dimmed)" }}>—</span>}
                    </Table.Td>
                    <Table.Td>{s.is_active ? <Badge color="green">Aktif</Badge> : <Badge color="gray">Pasif</Badge>}</Table.Td>
                    <Table.Td onClick={(e) => e.stopPropagation()}>
                      <Group gap={4}>
                        <ActionIcon variant="subtle" title="Detay" onClick={() => setSelectedId(s.id)}>
                          <IconEye size={16} />
                        </ActionIcon>
                        <ActionIcon variant="subtle" title="Düzenle" onClick={(e) => startEdit(s, e)}>
                          <IconEdit size={16} />
                        </ActionIcon>
                        <ActionIcon variant="subtle" color="red" title="Sil" onClick={(e) => handleDelete(s.id, e)}>
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
          </Table.ScrollContainer>
        </Card>
      )}

      <ResourceForm
        opened={opened}
        onClose={close}
        resource="suppliers"
        title={editing ? "Tedarikçi Düzenle" : "Yeni Tedarikçi"}
        fields={fields}
        editId={editing?.id || null}
        initialValues={editing || undefined}
      />
    </Stack>
  );
}
